import { IAffiliateManager, ICreateAffiliate, IMessenger, IUser } from "@/api/types/user";
import { IBaseAutocomplete } from "@/types";
import {
    createEmployeeAffiliate,
    createOrAddMasterAccountV3,
    getActiveUserSearch,
    getAffiliateSearch,
    getTrafficSource,
    getUsersMasterAccountV3,
} from "@/api/user";
import { translatePartnerStatus } from "@/utils/translate";
import { getEmployeesOfferVerticals } from "@/api/offers";
import { showNotification, showServerError } from "@/utils";
import store from "@/store";
import router from "@/router";
import { subYears } from 'date-fns';
import {
    MANAGER_FROM_MASTER_HINT,
    MASTER_WITHOUT_MANAGER_HINT,
    withManagerRejectionMessage,
} from "@/utils/managerAssignment";

export enum AFFILIATE_FIELD {
    TEXT = 'text',
    PASSWORD = 'password',
    SELECT = 'select',
    DATE = 'date',
    TAG_SELECT = 'tag',
    MESSENGER = 'messenger',
    PHONE = 'phone',
    SWITCH = 'switch'
}

export interface IAffiliateField {
    text: string;
    property: string;
    type: AFFILIATE_FIELD;
    validateRule?: any;
}

interface ISelectField {
    [key: string]: {
        data: IBaseAutocomplete,
        items?: any
        api?: () => Promise<any>
    }
}

const passwordRegexp = new RegExp('(?=.*[A-Za-z]|[0-9]|[#?!@$()%^&*-])(?!.*[А-Яа-яЁё ]).{8,16}$');
const emailRegexp = new RegExp('^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$');
const date18YearsAgo = subYears(new Date(), 18);

const VALIDATION_RULES = {
    TEXT: (v) => !!v,
    PASSWORD: (v) => !!v && passwordRegexp.test(v),
    EMAIL: (v) => !!v && emailRegexp.test(v),
    ARRAY: (v) => !!v && (Array.isArray(v) && v.length > 0),
    BIRTHDAY: () => ({ start: null, end: date18YearsAgo }),
};

export class CreateAffiliate implements ICreateAffiliate {
    account_manager_id: number;
    first_name: string;
    last_name: string;
    nick_name: string;
    status: string;
    email: string;
    password: string;
    messenger: IMessenger;
    birthday: string;
    phone: string;
    country: string;
    city: string;
    language: string;
    master_id: number;
    traffic_source_ids: number[];
    vertical_ids: number[];
    tag_ids: number[];
    newcomer: boolean;
    /**
     * Служебное состояние формы, в DTO не уходит: getDto() собирает тело запроса из полей
     * экземпляра, поэтому всё, чего нет в ICreateAffiliate, держим здесь и вычищаем по имени.
     */
    masterState: {
        /** Менеджер мастер-аккаунта: подставляется автоматически и не редактируется */
        manager: IAffiliateManager | null,
        /** Мастер прочитан и его менеджер известен - в том числе то, что менеджера нет */
        isResolved: boolean,
    } = { manager: null, isResolved: false };

    constructor(affiliate: ICreateAffiliate) {
        this.account_manager_id = affiliate.account_manager_id || this.getDefaultManager;
        this.first_name = affiliate.first_name || '';
        this.last_name = affiliate.last_name || '';
        this.nick_name = affiliate.nick_name || '';
        this.status = affiliate.status || '';
        this.email = affiliate.email || '';
        this.password = affiliate.password || '';
        this.messenger = affiliate.messenger || { type: '', account: '' };
        this.birthday = affiliate.birthday || '';
        this.phone = affiliate.phone || '';
        this.country = affiliate.country || '';
        this.city = affiliate.city || '';
        this.language = affiliate.language || '';
        this.master_id = affiliate.master_id;
        this.traffic_source_ids = affiliate.traffic_source_ids || [];
        this.vertical_ids = affiliate.vertical_ids || [];
        this.tag_ids = affiliate.tag_ids || [];
        this.newcomer = affiliate.newcomer || false;
    }

    get getOptions(): { [key: string]: IAffiliateField[] } {
        return {
            main: [
                { text: 'Имя', property: 'first_name', type: AFFILIATE_FIELD.TEXT, validateRule: VALIDATION_RULES.TEXT },
                { text: 'Фамилия',property: 'last_name',type: AFFILIATE_FIELD.TEXT,validateRule: VALIDATION_RULES.TEXT },
                { text: 'Никнейм', property: 'nick_name', type: AFFILIATE_FIELD.TEXT },
                { text: 'Пароль',property: 'password',type: AFFILIATE_FIELD.PASSWORD,validateRule: VALIDATION_RULES.PASSWORD },
                { text: 'Менеджер', property: 'account_manager_id', type: AFFILIATE_FIELD.SELECT, validateRule: VALIDATION_RULES.TEXT },
                { text: 'Мастер-аккаунт', property: 'master_id', type: AFFILIATE_FIELD.SELECT },
                { text: 'Статус', property: 'status', type: AFFILIATE_FIELD.SELECT, validateRule: VALIDATION_RULES.TEXT },
                { text: 'Новорег', property: 'newcomer', type: AFFILIATE_FIELD.SWITCH },
            ],
            traffic: [
                { text: 'Вертикаль', property: 'vertical_ids', type: AFFILIATE_FIELD.SELECT, validateRule: VALIDATION_RULES.ARRAY },
                { text: 'Источник трафика', property: 'traffic_source_ids', type: AFFILIATE_FIELD.SELECT },
            ],
            contacts: [
                { text: 'E-mail', property: 'email', type: AFFILIATE_FIELD.TEXT, validateRule: VALIDATION_RULES.EMAIL },
                { text: 'Телефон', property: 'phone', type: AFFILIATE_FIELD.PHONE },
                { text: 'Мессенджер', property: 'messenger', type: AFFILIATE_FIELD.MESSENGER },
            ],
            other: [
                { text: 'Язык', property: 'language', type: AFFILIATE_FIELD.SELECT, validateRule: VALIDATION_RULES.TEXT },
                { text: 'Теги', property: 'tag_ids', type: AFFILIATE_FIELD.TAG_SELECT },
                { text: 'Страна', property: 'country', type: AFFILIATE_FIELD.SELECT },
                { text: 'Город', property: 'city', type: AFFILIATE_FIELD.TEXT },
                { text: 'Дата рождения', property: 'birthday', type: AFFILIATE_FIELD.DATE, validateRule: VALIDATION_RULES.BIRTHDAY },
            ],
        };
    }

    getSelectData: ISelectField = {
        account_manager_id: {
            data: {
                apiMethod: getActiveUserSearch,
                template: (i) => `${i.id} ${i.first_name} ${i.last_name}`,
                itemValue: 'id',
                rules: [VALIDATION_RULES.TEXT],
            },
        },
        master_id: {
            data: {
                apiMethod: getAffiliateSearch,
                template: (i) => `${i.id} ${i.first_name} ${i.last_name}`,
                itemValue: 'id',
            },
        },
        status: {
            data: { itemText: 'text', itemValue: 'id', rules: [VALIDATION_RULES.TEXT] },
            items: this.getStatusItems,
        },
        vertical_ids: {
            data: { itemText: 'name', itemValue: 'id', multiple: true, rules: [VALIDATION_RULES.ARRAY] },
            api: getEmployeesOfferVerticals,
        },
        traffic_source_ids: {
            data: { itemText: 'name', itemValue: 'id', multiple: true },
            api: getTrafficSource,
        },
        language: {
            data: { itemText: 'text', itemValue: 'id', rules: [VALIDATION_RULES.TEXT] },
            items: [{ id: 'ru', text: 'Русский' }, { id: 'en', text: 'English' }],
        },
        country: {
            data: { itemText: 'name', itemValue: 'code' },
            items: store.getters['catalogueModule/GET_COUNTRIES'],
        },
        city: {
            data: { itemText: 'text', itemValue: 'id' },
            items: [],
        },
    };

    get getStatusItems(): { id: string, text: string }[] {
        const items = [{ id: 'active' }, { id: 'pending' }, { id: 'deleted' }, { id: 'blocked' }, { id: 'rejected' }];
        return items.map(i => ({ id: i.id, text: translatePartnerStatus(i.id).text }));
    }

    /** Мастер выбран - менеджер приходит от него, присланный account_manager_id бекенд игнорирует */
    get isManagerFromMaster(): boolean {
        return !!this.master_id && this.masterState.isResolved;
    }

    get masterManagerName(): string {
        const manager = this.masterState.manager;
        return manager ? `${manager.id} ${manager.first_name} ${manager.last_name}`.trim() : 'Не указан у мастер-аккаунта';
    }

    get managerFromMasterHint(): string {
        return this.masterState.manager ? MANAGER_FROM_MASTER_HINT : MASTER_WITHOUT_MANAGER_HINT;
    }

    /**
     * Менеджер слейва совпадает с менеджером мастера, поэтому при выборе мастер-аккаунта
     * менеджер подставляется от него, а поле блокируется.
     */
    async setManagerFromMaster(masterId: number | null): Promise<void> {
        if (!masterId) {
            this.masterState = { manager: null, isResolved: false };
            this.account_manager_id = this.getDefaultManager;
            return;
        }

        try {
            const { data } = await getUsersMasterAccountV3({ account_id: masterId });
            // выбранный аккаунт сам может быть слейвом - менеджера берём у его мастера
            const isSlave = data.master_id !== null && data.master_id !== masterId;
            const master = isSlave
                ? (await getUsersMasterAccountV3({ account_id: data.master_id! })).data
                : data;

            this.masterState = { manager: master.manager ?? null, isResolved: true };
            // Менеджера у мастера нет - наследовать нечего, обязательное поле обнуляем.
            // Иначе в нём остался бы менеджер по умолчанию: партнёр создался бы с ним,
            // а привязка к мастеру потом отвалилась бы, оставив висячий аккаунт.
            this.account_manager_id = this.masterState.manager?.id ?? 0;
        } catch (err) {
            this.masterState = { manager: null, isResolved: false };
            showServerError(err, 'Менеджер мастер-аккаунта не определён');
        }
    }

    get getDefaultManager(): number {
        const user = store.getters['authModule/GET_USER'] as IUser;
        return user.account_id!;
    }

    async getAllItems(): Promise<void> {
        await Promise.all(Object.values(this.getSelectData).map(async item => item.api && (item.items = await item.api())))
            .catch((err) => showServerError(err));
    }

    private get getFields(): IAffiliateField[] {
        return Object.values(this.getOptions).flat().filter(item => !!item.validateRule);
    }

    get isDisableCreate(): boolean {
        const isValidFields = this.getFields.every(field =>
            field.validateRule === undefined ? true : field.validateRule(this[field.property]));
        return !isValidFields;
    }

    get getCountRequiredFields(): number {
        return this.getFields.filter(item => !item.validateRule(this[item.property])).length;
    }

    get isShowPasswordHint(): boolean {
        const passwordOpt = this.getFields.find(item => item.property === 'password');
        return !!passwordOpt ? !passwordOpt.validateRule(this.password) : false;
    }

    async setMaster(id: number): Promise<void> {
        const { data } = await getUsersMasterAccountV3({ account_id: this.master_id });
        await createOrAddMasterAccountV3({
            account_id: data.master_id !== null ? data.master_id : this.master_id,
            account_ids: [id],
        }).catch(err => {
            throw withManagerRejectionMessage(err);
        });
    }

    async createAffiliate(): Promise<void> {
        const getDto = () => {
            const dto = Object.fromEntries(Object.entries(this));

            const isDelete = (key: string) => {
                return ['master_id', 'getSelectData', 'masterState'].includes(key) || !dto[key] ||
                    (Array.isArray(dto[key]) && dto[key].filter(Boolean).length === 0) ||
                    (key === 'messenger' && (dto[key].account === '' || dto[key].type === ''));
            };

            for (const key in dto) {
                isDelete(key) && (delete dto[key]);
            }
            return dto;
        };

        try {
            const dto = { ...getDto(), ...{ newcomer: this.newcomer } };
            const { id } = await createEmployeeAffiliate(dto as ICreateAffiliate);
            if (this.master_id) await this.setMaster(id);
            showNotification('Партнёр успешно создан');
            await router.push({
                name: 'affiliates-list',
                query: {
                    manager_ids: `${this.account_manager_id!}`,
                },
            });
        } catch (err) {
            showServerError(withManagerRejectionMessage(err), 'Ошибка создания партнёра');
        }
    }
}


